// Key chords: what a key press means to the keyboard router, and the key
// specs keymaps are written in.

/** One key press as the router reads it: the produced character (`KeyboardEvent.key`) and the modifiers held as modifiers. Shift is part of the character (`G`, `?`). */
export interface Chord {
  key: string;
  ctrl: boolean;
  meta: boolean;
}

/** Keymap characters that some layouts type with AltGr (Windows/Linux) or Option (macOS). */
const LAYOUT_CHARS = new Set(['[', ']', '\\', '/', '?', '{', '}', '|', '~', '@']);

type KeyLike = Pick<KeyboardEvent, 'key' | 'altKey' | 'ctrlKey' | 'metaKey' | 'isComposing'> & {
  getModifierState?: (key: string) => boolean;
  keyCode?: number;
};

/**
 * Whether a key belongs to an IME composition. WebKit ends the composition
 * before the keydown of the key that ends it, so that key reads
 * `isComposing` false; its `keyCode` is still 229.
 */
export function composing(e: { isComposing: boolean; keyCode?: number }): boolean {
  return e.isComposing || e.keyCode === 229;
}

/** Keys that only modify the next one: pressed alone, they mean nothing (Shift on the way to `G` or `gF`). */
const MODIFIERS = new Set(['Shift', 'Control', 'Alt', 'AltGraph', 'Meta', 'CapsLock']);

/**
 * The chord a key event means, or null when the router leaves it alone:
 * IME composition (including the key that ends one), a modifier key
 * pressed alone, and Alt combinations.
 *
 * `[`, `]` and `\` need AltGr on German, French, Spanish, Swiss and Nordic
 * layouts (reported as Ctrl+Alt on Windows, Alt or AltGraph elsewhere) and
 * Option on macOS. `e.key` already holds the character, so for those
 * characters the Alt (and AltGr's Ctrl) that produced them are ignored.
 * Ctrl alone with `[` stays a Ctrl chord.
 */
export function chordOf(e: KeyLike): Chord | null {
  if (composing(e) || MODIFIERS.has(e.key)) return null;
  const altGr = !!e.getModifierState?.('AltGraph');
  if (LAYOUT_CHARS.has(e.key) && (e.altKey || altGr)) return e.metaKey ? null : { key: e.key, ctrl: false, meta: false };
  if (e.altKey) return null;
  return { key: e.key, ctrl: e.ctrlKey, meta: e.metaKey };
}

/** Whether a chord has no Ctrl or Meta: only such chords make up sequences. */
export const plain = (c: Chord) => !c.ctrl && !c.meta;

export const sameChord = (a: Chord, b: Chord) => a.key === b.key && a.ctrl === b.ctrl && a.meta === b.meta;

/** Key names a spec may use for keys that are hard to write. */
const NAMED: Record<string, string> = { Space: ' ' };

/** The `KeyboardEvent.key` a spec's key name stands for (`Space` is `' '`). */
export const keyName = (name: string) => NAMED[name] ?? name;

/**
 * The chord sequences a key spec names; each is one chord or a sequence of
 * plain keys. A spec is space-separated chords (`g o`, `] c`), each a key
 * (`j`, `G`, `Enter`, `Space`, `ArrowLeft`) after optional `Ctrl+`,
 * `Meta+` or `Mod+` (Meta or Ctrl: one sequence for each). Shift is
 * written as the character it produces; Alt combinations are not
 * dispatched. Throws on a spec the parser cannot read.
 */
export function parseSpec(spec: string): Chord[][] {
  const parts = spec.split(' ');
  if (!spec || parts.some((p) => !p)) throw new Error(`key spec "${spec}": keys, space-separated`);
  const chords = parts.map((p) => {
    const plus = p.length > 1 ? p.lastIndexOf('+', p.length - 2) : -1;
    const mods = plus < 0 ? [] : p.slice(0, plus).split('+');
    const name = plus < 0 ? p : p.slice(plus + 1);
    for (const m of mods) if (!['Ctrl', 'Meta', 'Mod'].includes(m)) throw new Error(`key spec "${spec}": no ${m} combinations`);
    return { key: keyName(name), mods };
  });
  if (chords.length > 1 && chords.some((c) => c.mods.length)) throw new Error(`key spec "${spec}": a sequence is plain keys`);
  const [first] = chords;
  const mod = first.mods.includes('Mod');
  const make = (meta: boolean, ctrl: boolean) => chords.map((c, i) => (i === 0 ? { key: c.key, ctrl, meta } : { key: c.key, ctrl: false, meta: false }));
  const ctrl = first.mods.includes('Ctrl');
  const meta = first.mods.includes('Meta');
  return mod ? [make(true, ctrl), make(meta, true)] : [make(meta, ctrl)];
}

const SHOWN: Record<string, string> = {
  ' ': 'Space',
  Escape: 'Esc',
  ArrowLeft: '←',
  ArrowRight: '→',
  ArrowUp: '↑',
  ArrowDown: '↓',
};

/** How the guide writes a key (`Space`, `Esc`, `←`). */
export const keyLabel = (key: string) => SHOWN[key] ?? key;

const chordLabel = (c: Chord) => `${c.meta ? '⌘ ' : ''}${c.ctrl ? 'Ctrl ' : ''}${keyLabel(c.key)}`;

/** How the key guide writes a spec: one label per chord sequence it names (`Mod+Enter` is `⌘ Enter` and `Ctrl Enter`). */
export function labels(spec: string): string[] {
  return parseSpec(spec).map((seq) => seq.map(chordLabel).join(''));
}
